import React from "react";
import { useTranslation } from "react-i18next";
import { Story, Participant } from "../../common/types/domain";
import { useTags } from "../../common/contexts/TagsContext";
import { RecentLogsPanel } from "../../logger/components/RecentLogsPanel";

interface StreamFeedProps {
  externalStories?: Story[];
  externalParticipants?: Participant[];
}

export const StreamFeed: React.FC<StreamFeedProps> = ({
  externalStories = [],
  externalParticipants = [],
}) => {
  const { t } = useTranslation();
  const { getTagLabel, getTagColor, getCameraName } = useTags();

  const globalLogs = externalStories
    .flatMap((s) =>
      (s.logs || []).map((l: any) => ({
        ...l,
        author: l.author || s.author,
        // Inherit story-level metadata so LogCard detail panel works
        storyTags: l.storyTags || (s as any).tags || [],
        storyRating: l.storyRating ?? (s as any).rating ?? 0,
        storyParticipants: l.storyParticipants || (s as any).participants || [],
        entryTags: l.entryTags || l.tags || [],
        streams: l.streams || [],
        storyName: l.storyName || s.title || "",
        eventName: l.eventName || "",
        rundownName: l.rundownName || "",
      })),
    )
    .sort((a: any, b: any) => (b.createdAt || 0) - (a.createdAt || 0));

  const getParticipantName = (id: string) => {
    const p = externalParticipants.find((p) => p.id.toLowerCase() === id.toLowerCase());
    if (!p) return id;
    return p.number ? `${p.number} ${p.alias || p.name}` : p.alias || p.name;
  };

  return (
    <div
      data-testid="stream-panel"
      style={{
        flex: 1,
        minHeight: 0,
        height: "100%",
        background: "var(--color-panel-bg)",
        borderRadius: "var(--border-radius-lg)",
        border: "1px solid var(--color-panel-border)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        boxShadow: "var(--shadow-card)",
      }}
    >
      <div
        data-testid="stream-header"
        style={{
          padding: "15px",
          borderBottom: "1px solid var(--color-panel-border)",
          background: "var(--color-bg-base)",
          display: "flex",
          alignItems: "center",
          gap: "10px",
        }}
      >
        <i
          className="bx bx-broadcast"
          style={{ fontSize: "1.2rem", color: "var(--color-accent-primary)" }}
        ></i>
        <span
          style={{
            fontWeight: 900,
            letterSpacing: "2px",
            fontSize: "1rem",
            color: "#ffffff",
          }}
        >
          {t("controller.dashboard.stream")}
        </span>
      </div>
      <div
        data-testid="stream-logs-list"
        style={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          padding: "8px",
        }}
      >
        {globalLogs.length === 0 ? (
          <div
            data-testid="stream-empty-state"
            style={{
              padding: "40px",
              textAlign: "center",
              color: "var(--color-text-muted)",
            }}
          >
            {t("controller.dashboard.waitingForLogs")}
          </div>
        ) : (
          <RecentLogsPanel
            logs={globalLogs as any}
            participants={externalParticipants}
            getCameraName={getCameraName}
            getParticipantName={getParticipantName}
            getTagColor={getTagColor}
            getTagLabel={getTagLabel}
            newestAt="top"
            showHeader={false}
          />
        )}
      </div>
    </div>
  );
};
